/**
 * What a procedure's materials cost, and what is left of its price — STORY-175a,
 * decisions MC-D3–MC-D6. **The one copy of this rule**: the procedure panel
 * reads it, and anything that later needs a tray's cost reads it too.
 *
 * ── What costs money (MC-D3) ────────────────────────────────────────────────
 *
 * A **countable** use costs its pack price ÷ the uses in a pack (IP-D3) — the
 * cost sheet's "Gloves (100 pcs/package) 2.50" is 0.025 a glove. A **bulk** use
 * costs its pack price ÷ about how many uses a pack serves (MC-D4), because a
 * bulk item's own `usesPerStockUnit` is 1 by design (TP7) and would charge the
 * whole tube to one patient. A **reusable** instrument costs nothing per use.
 *
 * ── Exact, and rounded once (MC-D5) ─────────────────────────────────────────
 *
 * A use is usually a fraction of a minor unit, so every figure here is an exact
 * fraction and only what is *shown* is rounded — half up, to a whole minor unit.
 * Three lines of 0.025 total 0.075 and show as 0.08; rounding each line first
 * would show 0.09. No float touches money anywhere in this file.
 *
 * ── For one sitting on one tooth (MC-D6) ────────────────────────────────────
 *
 * A per-tooth material counts once. The procedure panel describes a procedure,
 * not a booking, so nothing here knows how many teeth will be treated.
 */
import type { InventoryKind } from "./index";

/** An exact amount of money: `num / den` minor units, `den` always positive. */
export interface ExactMinor {
  num: bigint;
  den: bigint;
}

/** What costing one tray line needs to know about it and its item. */
export interface CostedLine {
  /** In uses, before scope would multiply it — counted once here (MC-D6). */
  quantity: number;
  itemKind: InventoryKind;
  /** One pack, in minor units (IP-D1). */
  itemPriceCents: number;
  /** Uses in one pack — exact for a countable item. */
  itemUsesPerStockUnit: number;
  /** About how many uses one pack serves — set on bulk items only (MC-D4). */
  itemEstimatedUses: number | null;
}

/** A tray line as the server sends it: its money only with Accounting (QF-D1). */
export type MaybeCostedLine = Omit<
  CostedLine,
  "itemPriceCents" | "itemUsesPerStockUnit" | "itemEstimatedUses"
> &
  Partial<Pick<CostedLine, "itemPriceCents" | "itemUsesPerStockUnit" | "itemEstimatedUses">>;

/**
 * **The lines with their money, or null when the server left it out** (QF-D1).
 *
 * All or nothing: a tray costed from some of its lines would read a profit that
 * ignores the rest, which is worse than reading none.
 */
export function costedLines<T extends MaybeCostedLine>(
  lines: readonly T[],
): (T & CostedLine)[] | null {
  const costed: (T & CostedLine)[] = [];
  for (const line of lines) {
    if (line.itemPriceCents === undefined || line.itemUsesPerStockUnit === undefined) return null;
    costed.push({
      ...line,
      itemPriceCents: line.itemPriceCents,
      itemUsesPerStockUnit: line.itemUsesPerStockUnit,
      itemEstimatedUses: line.itemEstimatedUses ?? null,
    });
  }
  return costed;
}

const ZERO: ExactMinor = { num: 0n, den: 1n };

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x === 0n ? 1n : x;
}

function reduce(value: ExactMinor): ExactMinor {
  const g = gcd(value.num, value.den);
  return { num: value.num / g, den: value.den / g };
}

function add(a: ExactMinor, b: ExactMinor): ExactMinor {
  return reduce({ num: a.num * b.den + b.num * a.den, den: a.den * b.den });
}

/**
 * The uses one pack is costed over, or null for a line that costs nothing.
 *
 * A bulk item missing its estimate cannot happen past the database's CHECK; it
 * is treated as costing nothing rather than throwing, so one bad row can never
 * blank a whole panel.
 */
function usesCostedOver(line: CostedLine): number | null {
  switch (line.itemKind) {
    case "COUNTABLE":
      return line.itemUsesPerStockUnit;
    case "BULK":
      return line.itemEstimatedUses;
    case "REUSABLE":
      return null;
  }
}

/** One line's exact cost. A reusable instrument's is zero (MC-D3). */
export function lineCost(line: CostedLine): ExactMinor {
  const uses = usesCostedOver(line);
  if (uses === null || uses <= 0) return ZERO;
  return reduce({
    num: BigInt(line.quantity) * BigInt(line.itemPriceCents),
    den: BigInt(uses),
  });
}

/** A whole tray's exact cost — the sum of exact lines, never of rounded ones. */
export function trayCost(lines: readonly CostedLine[]): ExactMinor {
  return lines.reduce((sum, line) => add(sum, lineCost(line)), ZERO);
}

/**
 * Round an exact amount to whole minor units, half up — away from zero for a
 * negative amount, so a loss rounds the same way a gain does.
 */
export function roundMinor(value: ExactMinor): number {
  const negative = value.num < 0n;
  const magnitude = negative ? -value.num : value.num;
  const rounded = (2n * magnitude + value.den) / (2n * value.den);
  return Number(negative ? -rounded : rounded);
}

/**
 * What the procedure panel shows: the tray's cost, rounded once from the exact
 * sum (MC-D5), and the profit.
 *
 * **Profit is the price minus that rounded total**, not a second rounding of
 * the exact difference. The price is already whole minor units, so this is
 * still one rounding — and the three figures on the screen always reconcile.
 * Rounding the difference separately would show, for a tray costing exactly
 * half a minor unit, *50.00 − 0.01 = 50.00*. May be negative, shown as one.
 */
export function procedureProfit(
  priceCents: number,
  lines: readonly CostedLine[],
): { materialsCents: number; profitCents: number } {
  const materialsCents = roundMinor(trayCost(lines));
  return { materialsCents, profitCents: priceCents - materialsCents };
}
